import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  trackSellersCarouselView,
  trackSellersCarouselAgentClick,
  trackSellersCarouselOfferClick,
  trackSellersCarouselAllOffersClick,
} from './tracking';
import { SellersBannerCarousel } from '../../components/SellersBannerCarousel';
import { fetchTrackSpecialPromoShow } from '../../services/fetchTrackSpecialPromoShow';
import { useApplicationContext } from '../../utils/applicationContext';
import { SellersAdfoxBannerContainer } from '../SellersAdfoxBannerContainer';
import { selectRegionId } from 'shared/selectors/location';
import { selectJsonQuery } from 'shared/selectors/results';
import { selectSpecialPromoSecondaryOffers } from 'shared/selectors/specialPromoSecondaryOffers';

export const SellersBannerContainer: React.FC = () => {
  const specialPromoSecondaryOffers = useSelector(selectSpecialPromoSecondaryOffers);
  const { needShowAdfoxBlock, isCarouselVisible, carousel } = specialPromoSecondaryOffers ?? {};

  const regionId = useSelector(selectRegionId);
  const agentId = carousel?.agent?.realtyUserId as number;
  const jsonQuery = useSelector(selectJsonQuery);
  const { httpApi, logger, config } = useApplicationContext();
  const isEnabled = config.get<boolean>('SellersSpecialProject.Enabled') || false;

  const viewedOfferIdsRef = React.useRef(new Set<number>());

  const handleCarouselView = React.useCallback(
    (offers: Array<{ id: number; position: number }>) => {
      trackSellersCarouselView(offers, agentId);
      offers.forEach(({ id }) => viewedOfferIdsRef.current.add(id));

      fetchTrackSpecialPromoShow({
        httpApi,
        logger,
        parameters: {
          agentId,
          regionId: jsonQuery.region?.value[0] ?? regionId,
        },
      });
    },
    [agentId, httpApi, jsonQuery.region?.value, logger, regionId],
  );

  const handleCarouselSlideChange = React.useCallback(
    (offers: Array<{ id: number; position: number }>) => {
      const newOffers = offers.filter(({ id }) => !viewedOfferIdsRef.current.has(id));

      if (newOffers.length === 0) {
        return;
      }

      trackSellersCarouselView(newOffers, agentId);
      newOffers.forEach(({ id }) => viewedOfferIdsRef.current.add(id));
    },
    [agentId],
  );

  const handleAgentClick = React.useCallback(() => {
    trackSellersCarouselAgentClick(agentId);
  }, [agentId]);

  const handleOfferClick = React.useCallback(
    (offerId: number, position: number) => {
      trackSellersCarouselOfferClick({ offerId, position, agentId });
    },
    [agentId],
  );

  const handleAllOffersClick = React.useCallback(() => {
    trackSellersCarouselAllOffersClick(agentId);
  }, [agentId]);

  if (!isEnabled) {
    return null;
  }

  if (needShowAdfoxBlock) {
    return <SellersAdfoxBannerContainer />;
  }

  if (isCarouselVisible && carousel) {
    return (
      <SellersBannerCarousel
        carousel={carousel}
        source="media_agent_banner"
        onView={handleCarouselView}
        onSlideChange={handleCarouselSlideChange}
        onAgentClick={handleAgentClick}
        onOfferClick={handleOfferClick}
        onAllOffersClick={handleAllOffersClick}
      />
    );
  }

  return null;
};
